<template>
    <article class="rma-item" :class="`rma-item--${item.status}`">
        <header class="rma-item__header">
            <div class="rma-item__heading">
                <h3 class="rma-item__title">{{ type.name }}</h3>
                <p v-if="type.instructions" class="rma-item__instructions">{{ type.instructions }}</p>
                <div class="rma-item__formats">Formatos aceitos: {{ formats }} · até 200 MB por arquivo</div>
            </div>
            <pa-badge :variant="status.tone" :text="status.label" />
        </header>

        <div class="rma-item__body">
            <div v-if="clarification" class="rma-note rma-note--info">
                <v-icon small>mdi-comment-question-outline</v-icon>
                <div>
                    <div><strong>Pedido de esclarecimento:</strong> {{ clarification.question }}</div>
                    <div class="rma-note__sub">Responda em "Pedidos de esclarecimento", no topo da página.</div>
                </div>
            </div>

            <div v-if="item.status === 'returned'" class="rma-note rma-note--danger">
                <v-icon small>mdi-alert-circle-outline</v-icon>
                <div>
                    <div v-if="item.review_status === 'incomplete' || item.review_status === 'wrong'" class="rma-note__title">
                        {{ item.review_status === "wrong" ? "Documento rejeitado" : "Documento incompleto" }}
                    </div>
                    <div><strong>Motivo da devolução:</strong> {{ item.return_message || "não informado" }}</div>
                    <div class="rma-note__sub">
                        <template v-if="item.returned_at">Devolvido em {{ formatDateTime(item.returned_at) }}. </template>
                        Envie o arquivo corrigido abaixo.
                    </div>
                </div>
            </div>

            <div v-else-if="item.status === 'not_applicable'" class="rma-note rma-note--neutral">
                <v-icon small>mdi-minus-circle-outline</v-icon>
                <div>
                    <div><strong>Justificativa:</strong> {{ item.justification || "—" }}</div>
                    <div class="rma-note__sub">Se o documento existir, basta enviar o arquivo: a marcação "não se aplica" é desfeita.</div>
                </div>
            </div>

            <div v-else-if="item.status === 'accepted'" class="rma-note rma-note--success">
                <v-icon small>mdi-check-circle-outline</v-icon>
                <div>
                    Conferido pela Administração Judicial<template v-if="item.accepted_at"> em {{ formatDateTime(item.accepted_at) }}</template>.
                    Não é possível enviar nem excluir arquivos deste documento.
                </div>
            </div>

            <ul v-if="item.files && item.files.length" class="rma-files">
                <li v-for="file in visibleFiles" :key="file.id" class="rma-file">
                    <v-icon size="20" class="rma-file__icon">{{ fileIcon(file.original_name) }}</v-icon>
                    <div class="rma-file__main">
                        <div class="rma-file__name">{{ file.original_name }}</div>
                        <div class="rma-file__meta">
                            <span>{{ formatBytes(file.size) }} · {{ formatDateTime(file.uploaded_at) }}</span>
                            <span v-if="file.uploaded_by_type === 'admin'" class="rma-tag rma-tag--info">enviado pela Administração Judicial</span>
                            <span v-if="file.uploaded_after_deadline" class="rma-tag rma-tag--warning">fora do prazo</span>
                        </div>
                    </div>
                    <div class="rma-file__actions">
                        <v-btn v-if="!link" icon small title="Baixar arquivo" aria-label="Baixar arquivo" :loading="busy === `download-${file.id}`" @click="download(file)">
                            <v-icon small>mdi-download</v-icon>
                        </v-btn>
                        <v-btn v-if="canDelete(file)" icon small title="Excluir arquivo" aria-label="Excluir arquivo"
                            :loading="busy === `delete-${file.id}`" @click="remove(file)">
                            <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                    </div>
                </li>
                <li v-if="item.files.length > FILES_PREVIEW" class="rma-file rma-file--more">
                    <v-btn text small color="primary" @click="showAllFiles = !showAllFiles">
                        <v-icon left small>{{ showAllFiles ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                        {{ showAllFiles ? "Mostrar menos" : `Mostrar todos os ${item.files.length} arquivos` }}
                    </v-btn>
                </li>
            </ul>

            <!-- Link de envio: arquivos enviados pelo painel ou por outro link so aparecem na contagem -->
            <div v-if="link && item.other_files" class="rma-note rma-note--neutral">
                <v-icon small>mdi-file-multiple-outline</v-icon>
                <div>{{ item.other_files === 1 ? "1 arquivo já tinha sido enviado" : `${item.other_files} arquivos já tinham sido enviados` }} para este documento. Envie só o que estiver faltando.</div>
            </div>

            <div v-if="batch.files > 1 && activeUploads" class="rma-batch" role="status" aria-live="polite">
                <div class="rma-batch__head">
                    <span class="rma-batch__title">Enviando {{ batch.files }} arquivos</span>
                    <span class="rma-batch__percent">{{ batchPercent }}%</span>
                </div>
                <v-progress-linear :value="batchPercent" height="6" rounded color="#27306b" background-color="#e2e8f0"
                    aria-label="Progresso do envio dos arquivos"></v-progress-linear>
                <div class="rma-batch__sub">{{ batch.finished }} de {{ batch.files }} concluídos · {{ formatBytes(batchLoaded) }} de {{ formatBytes(batch.bytes) }}</div>
            </div>

            <ul v-if="uploads.length" class="rma-files rma-uploads">
                <li v-for="upload in uploads" :key="upload.uid" class="rma-file" :class="{ 'rma-file--error': upload.status === 'error' }">
                    <v-icon size="20" class="rma-file__icon">{{ upload.status === "error" ? "mdi-alert-circle-outline" : "mdi-file-upload-outline" }}</v-icon>
                    <div class="rma-file__main">
                        <div class="rma-file__name">{{ upload.name }}</div>
                        <div v-if="upload.status === 'error'" class="rma-file__error">{{ upload.error }}</div>
                        <template v-else>
                            <div class="rma-file__meta rma-upload__meta">
                                <span>{{ uploadStatusText(upload) }}</span>
                                <span v-if="upload.status === 'uploading'" class="rma-upload__percent">{{ upload.progress }}%</span>
                            </div>
                            <v-progress-linear :value="upload.progress" :indeterminate="upload.status === 'saving'" height="6" rounded
                                :color="upload.status === 'queued' ? '#cbd5e1' : '#27306b'" background-color="#e2e8f0" class="mt-1"
                                :aria-label="`Progresso do envio de ${upload.name}`"></v-progress-linear>
                        </template>
                    </div>
                    <div v-if="upload.status === 'error'" class="rma-file__actions">
                        <v-btn v-if="upload.retryable && !locked" small text @click="retry(upload)">Tentar de novo</v-btn>
                        <v-btn icon small title="Fechar aviso" aria-label="Fechar aviso" @click="dismiss(upload)">
                            <v-icon small>mdi-close</v-icon>
                        </v-btn>
                    </div>
                    <div v-else-if="upload.status === 'queued' || upload.status === 'uploading'" class="rma-file__actions">
                        <v-btn icon small title="Cancelar envio" aria-label="Cancelar envio" @click="cancel(upload)">
                            <v-icon small>mdi-close</v-icon>
                        </v-btn>
                    </div>
                </li>
            </ul>

            <rma-file-drop :accept="accept" :disabled="locked" :hint="dropHint"
                disabled-text="Documento conferido: envio encerrado" @files="onFiles" />
        </div>

        <footer v-if="type.has_template || canMarkNotApplicable" class="rma-item__footer">
            <v-btn v-if="type.has_template" small outlined :loading="busy === 'template'" @click="downloadTemplate">
                <v-icon left small>mdi-file-download-outline</v-icon>Baixar template
            </v-btn>
            <v-btn v-if="canMarkNotApplicable" small text class="rma-item__na" @click="naOpen = true">
                <v-icon left small>mdi-minus-circle-outline</v-icon>Não se aplica
            </v-btn>
        </footer>

        <rma-not-applicable-dialog v-model="naOpen" :item="item" @done="emitUpdated" />
    </article>
</template>

<script>
import axios from "axios";
import PaBadge from "@/components/plan-adhesion/PaBadge.vue";
import RmaFileDrop from "./RmaFileDrop.vue";
import RmaNotApplicableDialog from "./RmaNotApplicableDialog.vue";
import {
    acceptAttribute,
    acceptedExtensions,
    downloadFromUrl,
    errorMessage,
    fileIcon,
    formatBytes,
    formatDateTime,
    formatExtensions,
    formatRemaining,
    itemStatus,
    notifyError,
    notifySuccess,
    uploadFile,
    validateFile,
} from "./rma.util";

const MAX_PARALLEL = 3;
let uploadSeq = 0;

// Um documento do checklist do mes: arquivos enviados, envio de varios arquivos
// (para a API, que repassa ao storage; barra por arquivo e do lote), "nao se
// aplica" e template.
// Eventos: `updated` com { item, competence } apos cada acao e `busy` (boolean)
// enquanto houver envio em andamento.
const FILES_PREVIEW = 5;

export default {
    name: "RmaItemCard",

    components: { PaBadge, RmaFileDrop, RmaNotApplicableDialog },

    props: {
        item: { type: Object, required: true },
        // Pedido de esclarecimento aberto sobre este documento (a resposta fica no topo da pagina).
        clarification: { type: Object, default: null },
        // Link de envio sem login (token): usa as rotas publicas, sem baixar e sem "nao se aplica".
        link: { type: String, default: null },
    },

    data: () => ({
        FILES_PREVIEW,
        showAllFiles: false,
        uploads: [],
        busy: null,
        naOpen: false,
        // Lote em andamento (zera quando nao ha mais envio): arquivos e bytes.
        batch: { files: 0, finished: 0, bytes: 0, doneBytes: 0 },
    }),

    computed: {
        // Itens com muitos arquivos (ex.: um extrato por conta) mostram so os primeiros.
        visibleFiles() {
            const files = this.item.files || [];
            return this.showAllFiles || files.length <= FILES_PREVIEW ? files : files.slice(0, FILES_PREVIEW);
        },

        type() {
            return this.item.document_type || {};
        },
        status() {
            return itemStatus(this.item.status);
        },
        extensions() {
            return acceptedExtensions(this.item);
        },
        formats() {
            return formatExtensions(this.extensions);
        },
        accept() {
            return acceptAttribute(this.extensions);
        },
        locked() {
            return this.item.status === "accepted";
        },
        activeUploads() {
            return this.uploads.filter((upload) => ["queued", "uploading", "saving"].includes(upload.status)).length;
        },
        batchLoaded() {
            const inFlight = this.uploads
                .filter((upload) => upload.status === "uploading" || upload.status === "saving")
                .reduce((sum, upload) => sum + upload.loaded, 0);
            return Math.min(this.batch.bytes, this.batch.doneBytes + inFlight);
        },
        batchPercent() {
            return this.batch.bytes ? Math.floor((this.batchLoaded / this.batch.bytes) * 100) : 0;
        },
        canMarkNotApplicable() {
            if (this.link) return false;
            return ["pending", "returned"].includes(this.item.status) && !(this.item.files || []).length && !this.activeUploads;
        },
        dropHint() {
            if (this.locked) return "Se precisar substituir um arquivo, fale com a Administração Judicial.";
            return "Você pode enviar vários arquivos de uma vez.";
        },
    },

    watch: {
        activeUploads(count, previous) {
            if (!!count !== !!previous) this.$emit("busy", count > 0);
            if (!count && previous) {
                const sent = this.batch.finished;
                if (sent) notifySuccess(this, sent === 1 ? "Arquivo enviado." : `${sent} arquivos enviados.`);
                this.batch = { files: 0, finished: 0, bytes: 0, doneBytes: 0 };
            }
        },
    },

    created() {
        this.running = 0;
    },

    beforeDestroy() {
        this.uploads.forEach((upload) => upload.controller && upload.controller.abort());
        if (this.activeUploads) this.$emit("busy", false);
    },

    methods: {
        formatBytes,
        formatDateTime,
        fileIcon,

        canDelete(file) {
            // No link aparecem so os arquivos enviados por ele.
            if (this.link) return !this.locked;
            return file.uploaded_by_type === "client_admin" && !this.locked;
        },

        url(path) {
            return this.link ? `/api/rma-links/${this.link}${path}` : `/api/client/rma${path}`;
        },

        uploadStatusText(upload) {
            if (upload.status === "queued") return `${formatBytes(upload.size)} · na fila`;
            // Tudo saiu do navegador: a API termina de gravar no storage e registra.
            if (upload.status === "saving") return `${formatBytes(upload.size)} · concluindo envio`;
            const remaining = formatRemaining(upload.estimated);
            return `${formatBytes(upload.loaded)} de ${formatBytes(upload.size)}${remaining ? ` · ${remaining}` : ""}`;
        },

        emitUpdated(content) {
            // No link a API devolve a pagina inteira (todos os documentos).
            if (this.link && content) { this.$emit("updated", content); return; }
            if (content && content.item) this.$emit("updated", content);
        },

        onFiles(files) {
            if (this.locked) return;
            files.forEach((file) => {
                uploadSeq += 1;
                const problem = validateFile(file, this.extensions);
                this.uploads.push({
                    uid: uploadSeq,
                    name: file.name,
                    size: file.size,
                    progress: 0,
                    loaded: 0,
                    estimated: null,
                    status: problem ? "error" : "queued",
                    error: problem,
                    retryable: false,
                    file: problem ? null : file,
                    controller: null,
                });
                if (!problem) this.addToBatch(file.size);
            });
            this.pump();
        },

        addToBatch(size) {
            this.batch.files += 1;
            this.batch.bytes += size;
        },

        // Envio que nao terminou (erro ou cancelado) sai da barra do lote.
        removeFromBatch(size) {
            this.batch.files = Math.max(0, this.batch.files - 1);
            this.batch.bytes = Math.max(0, this.batch.bytes - size);
        },

        pump() {
            while (this.running < MAX_PARALLEL) {
                const next = this.uploads.find((upload) => upload.status === "queued");
                if (!next) return;
                this.running += 1;
                this.send(next).finally(() => {
                    this.running -= 1;
                    this.pump();
                });
            }
        },

        async send(upload) {
            upload.status = "uploading";
            upload.progress = 0;
            upload.loaded = 0;
            upload.estimated = null;
            upload.controller = new AbortController();
            try {
                const result = await uploadFile(this.url(`/items/${this.item.id}/files`), upload.file, {
                    signal: upload.controller.signal,
                    onProgress: ({ loaded, percent, estimated }) => {
                        upload.loaded = loaded;
                        upload.progress = percent;
                        upload.estimated = estimated;
                        if (percent >= 100) upload.status = "saving";
                    },
                });
                this.batch.finished += 1;
                this.batch.doneBytes += upload.size;
                this.uploads = this.uploads.filter((row) => row.uid !== upload.uid);
                this.emitUpdated(result.data.content);
            } catch (error) {
                if (axios.isCancel(error)) return;
                const response = error && error.response;
                const apiRefused = !!(response && response.data && response.data.content && response.data.content.error);
                // 413 tambem vem do proxy (Cloudflare), sem o corpo da API.
                const refused = apiRefused || !!(response && response.status === 413);
                upload.status = "error";
                upload.error = errorMessage(error, "Não foi possível enviar este arquivo. Tente de novo.");
                // Recusa (formato, tamanho, item conferido) nao adianta repetir;
                // falha de rede ou do storage, sim.
                upload.retryable = !refused || response.status >= 500;
                this.removeFromBatch(upload.size);
            } finally {
                upload.controller = null;
            }
        },

        // Na fila: sai da lista. Enviando: interrompe (a API descarta o que recebeu).
        cancel(upload) {
            if (upload.controller) upload.controller.abort();
            this.uploads = this.uploads.filter((row) => row.uid !== upload.uid);
            this.removeFromBatch(upload.size);
        },

        retry(upload) {
            if (!upload.file) return;
            upload.status = "queued";
            upload.error = null;
            upload.progress = 0;
            upload.loaded = 0;
            this.addToBatch(upload.size);
            this.pump();
        },

        dismiss(upload) {
            this.uploads = this.uploads.filter((row) => row.uid !== upload.uid);
        },

        async download(file) {
            this.busy = `download-${file.id}`;
            try {
                const result = await axios.get(`/api/client/rma/files/${file.id}/download`);
                downloadFromUrl(result.data.content.url, result.data.content.file_name || file.original_name);
            } catch (error) {
                notifyError(this, error, "Não foi possível baixar o arquivo.");
            } finally {
                this.busy = null;
            }
        },

        async remove(file) {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir arquivo",
                text: `Excluir "${file.original_name}" de "${this.type.name}"? Você pode enviar outro arquivo depois.`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;

            this.busy = `delete-${file.id}`;
            try {
                const result = await axios.delete(this.url(`/files/${file.id}`));
                this.emitUpdated(result.data.content);
                notifySuccess(this, "Arquivo excluído.");
            } catch (error) {
                notifyError(this, error, "Não foi possível excluir o arquivo.");
            } finally {
                this.busy = null;
            }
        },

        async downloadTemplate() {
            this.busy = "template";
            try {
                const result = await axios.get(this.url(`/document-types/${this.type.id}/template`));
                downloadFromUrl(result.data.content.url, result.data.content.file_name);
            } catch (error) {
                notifyError(this, error, "Não foi possível baixar o template.");
            } finally {
                this.busy = null;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.rma-item {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
}

.rma-item--returned {
    border-color: #fecaca;
}

.rma-item__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px 16px;
    padding: 18px 20px 12px;
}

.rma-item__heading {
    min-width: 0;
}

.rma-item__title {
    font-size: 15px;
    font-weight: 600;
    line-height: 1.4;
    color: #0f172a;
    margin: 0;
    overflow-wrap: anywhere;
}

.rma-item__instructions {
    font-size: 13.5px;
    color: #334155;
    line-height: 1.5;
    margin: 4px 0 0;
}

.rma-item__formats {
    font-size: 12.5px;
    color: #64748b;
    margin-top: 6px;
}

.rma-item__body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0 20px 18px;
}

.rma-item__footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border-top: 1px solid #eef2f6;
    background: #f8fafc;
    border-radius: 0 0 12px 12px;
}

.rma-item__na.v-btn {
    color: #475569 !important;
}

.rma-note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 13.5px;
    line-height: 1.5;

    .v-icon {
        margin-top: 2px;
        color: inherit !important;
    }
}

.rma-note__title {
    font-weight: 700;
    margin-bottom: 2px;
}

.rma-note__sub {
    font-size: 12.5px;
    opacity: 0.85;
    margin-top: 2px;
}

.rma-note--info {
    background: #f1effa;
    border: 1px solid #ddd6f3;
    color: #2b2050;
    margin-bottom: 8px;
}

.rma-note--danger {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
}

.rma-note--neutral {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #334155;
}

.rma-note--success {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #166534;
}

.rma-files {
    list-style: none;
    padding: 0;
    margin: 0;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}

.rma-file {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 9px 8px 9px 12px;

    & + & {
        border-top: 1px solid #eef2f6;
    }
}

.rma-file--error {
    background: #fef2f2;
}

.rma-file--more {
    justify-content: center;
    padding: 4px 8px;
}

.rma-file__icon {
    color: #64748b !important;
    margin-top: 1px;
    flex-shrink: 0;
}

.rma-file--error .rma-file__icon {
    color: #b91c1c !important;
}

.rma-file__main {
    flex: 1;
    min-width: 0;
}

.rma-file__name {
    font-size: 13.5px;
    font-weight: 500;
    color: #0f172a;
    line-height: 1.4;
    overflow-wrap: anywhere;
}

.rma-file__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
    font-size: 12.5px;
    color: #64748b;
    margin-top: 2px;
}

.rma-file__error {
    font-size: 12.5px;
    color: #b91c1c;
    margin-top: 2px;
}

.rma-batch {
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
}

.rma-batch__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
}

.rma-batch__title {
    font-size: 13.5px;
    font-weight: 600;
    color: #0f172a;
}

.rma-batch__percent,
.rma-upload__percent {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: #27306b;
}

.rma-batch__percent {
    font-size: 13.5px;
}

.rma-batch__sub {
    font-size: 12.5px;
    color: #64748b;
    margin-top: 6px;
    font-variant-numeric: tabular-nums;
}

.rma-upload__meta {
    flex-wrap: nowrap;
    align-items: flex-start;
    justify-content: space-between;
    font-variant-numeric: tabular-nums;

    > span:first-child {
        min-width: 0;
    }
}

.rma-upload__percent {
    flex-shrink: 0;
    margin-left: auto;
}

.rma-file__actions {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
}

.rma-tag {
    display: inline-flex;
    align-items: center;
    height: 20px;
    padding: 0 7px;
    border-radius: 5px;
    font-size: 11.5px;
    font-weight: 500;
    white-space: nowrap;
}

.rma-tag--info {
    background: #eef4ff;
    color: #1d4ed8;
}

.rma-tag--warning {
    background: #fdf3e7;
    color: #b45309;
}

@media (max-width: 700px) {
    .rma-item__header {
        flex-direction: column-reverse;
        padding: 16px 16px 10px;
    }

    .rma-item__body {
        padding: 0 16px 16px;
    }

    .rma-item__footer {
        padding: 10px 16px;
    }
}
</style>
